import type { GpsSample } from '../sensors';
import { detectRuntime } from './runtime';
// Static import — same reason as permissions.ts (Forgejo #13).
import { Geolocation } from '@capacitor/geolocation';

export type GpsSink = (s: GpsSample) => void;

export interface GpsSource {
  /**
   * Resolves once the first GPS sample arrives. Rejects on permission
   * denial or other startup error — callers can show a useful message
   * before they tell the user "recording started". After the first
   * sample, subsequent errors go to the optional onError callback so
   * the trip survives transient signal loss.
   */
  start: (sink: GpsSink, onError?: (e: GpsRuntimeError) => void) => Promise<void>;
  stop: () => Promise<void>;
}

export interface GpsRuntimeError {
  code: number;
  message: string;
}

export class GpsPermissionDeniedError extends Error {
  constructor() {
    super('Location permission denied');
    this.name = 'GpsPermissionDeniedError';
  }
}

export class GpsUnavailableError extends Error {
  constructor(message = 'Geolocation not available in this environment') {
    super(message);
    this.name = 'GpsUnavailableError';
  }
}

/**
 * Browser path — uses navigator.geolocation directly. Works on every
 * desktop browser, iOS Safari, and Android Chrome (the desktop PWA).
 */
class BrowserGpsSource implements GpsSource {
  private watchId: number | null = null;

  start(sink: GpsSink, onError?: (e: GpsRuntimeError) => void): Promise<void> {
    if (!navigator.geolocation) {
      return Promise.reject(new GpsUnavailableError());
    }
    return new Promise<void>((resolve, reject) => {
      let firstSampleSeen = false;
      this.watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const c = pos.coords;
          sink({
            lat: c.latitude,
            lon: c.longitude,
            speedKmh: c.speed !== null && c.speed >= 0 ? c.speed * 3.6 : -1,
            courseDeg: c.heading !== null && !Number.isNaN(c.heading) ? c.heading : null,
            accuracyM: c.accuracy,
            ts: pos.timestamp,
          });
          if (!firstSampleSeen) {
            firstSampleSeen = true;
            resolve();
          }
        },
        (e) => {
          if (!firstSampleSeen) {
            firstSampleSeen = true;
            if (e.code === 1) reject(new GpsPermissionDeniedError());
            else reject(new GpsUnavailableError(e.message));
            void this.stop();
          } else {
            onError?.({ code: e.code, message: e.message });
          }
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 30_000 },
      );
    });
  }

  async stop(): Promise<void> {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }
}

/**
 * Capacitor path — uses @capacitor/geolocation so position fixes route
 * through the Android native bridge (FusedLocationProviderClient). The
 * WebView's own navigator.geolocation works in browsers but does NOT
 * deliver samples reliably inside Capacitor — that's why Start was
 * doing nothing inside the APK before this fix.
 *
 * The plugin is dynamically imported so it doesn't bloat the web bundle.
 */
class CapacitorGpsSource implements GpsSource {
  private watchId: string | null = null;

  async start(sink: GpsSink, onError?: (e: GpsRuntimeError) => void): Promise<void> {
    let firstSampleSeen = false;
    return new Promise<void>((resolve, reject) => {
      // Watch fires the callback continuously. First (position, null)
      // call resolves the start() promise; subsequent (_, err) calls go
      // to onError. The await in watchPosition only waits for the
      // watcher to register, not for the first fix.
      Geolocation.watchPosition(
        { enableHighAccuracy: true, timeout: 30_000 },
        (position, err) => {
          if (err) {
            if (!firstSampleSeen) {
              firstSampleSeen = true;
              // Capacitor permission denial surfaces as a string message
              // containing 'permission' or 'denied' — defensive match.
              const msg = err.message ?? String(err);
              if (/permission|denied/i.test(msg)) {
                reject(new GpsPermissionDeniedError());
              } else {
                reject(new GpsUnavailableError(msg));
              }
              void this.stop();
            } else {
              onError?.({ code: 0, message: err.message ?? 'unknown' });
            }
            return;
          }
          if (!position) return;
          const c = position.coords;
          sink({
            lat: c.latitude,
            lon: c.longitude,
            speedKmh: c.speed !== null && c.speed >= 0 ? c.speed * 3.6 : -1,
            courseDeg: c.heading !== null && !Number.isNaN(c.heading) ? c.heading : null,
            accuracyM: c.accuracy,
            ts: position.timestamp,
          });
          if (!firstSampleSeen) {
            firstSampleSeen = true;
            resolve();
          }
        },
      )
        .then((id) => {
          this.watchId = id;
        })
        .catch((e: Error) => {
          if (!firstSampleSeen) {
            firstSampleSeen = true;
            reject(new GpsUnavailableError(e.message));
          }
        });
    });
  }

  async stop(): Promise<void> {
    if (this.watchId !== null) {
      try {
        await Geolocation.clearWatch({ id: this.watchId });
      } catch {
        // ignore — stop is fire-and-forget
      }
      this.watchId = null;
    }
  }
}

export function createGpsSource(): GpsSource {
  if (detectRuntime() === 'capacitor') {
    return new CapacitorGpsSource();
  }
  return new BrowserGpsSource();
}
